import { useCallback, useEffect, useRef, useState } from "react";

import styles from "./ReauthGate.module.css";
import { useSession } from "./session";
import { Button } from "../components/Button";
import { Modal } from "../components/Modal";
import { setReauthHandler } from "../data/api/client";

// Bridges the imperative `re_auth_required` retry (registered on the API client)
// with a passkey re-verification modal. When a sensitive action's request is
// rejected because the session is >15 min old, the client awaits this modal;
// re-verifying refreshes the session and resolves true, so the client retries
// once (auth-and-sessions.md §Re-authentication for sensitive actions).
export const ReauthGate = () => {
  const { signInWithPasskey } = useSession();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The pending re-auth's resolver; the client is awaiting the returned promise.
  const resolveRef = useRef<((verified: boolean) => void) | null>(null);

  useEffect(() => {
    setReauthHandler(
      () =>
        new Promise<boolean>((resolve) => {
          resolveRef.current = resolve;
          setError(null);
          setBusy(false);
          setOpen(true);
        }),
    );
    return () => setReauthHandler(null);
  }, []);

  const finish = useCallback((verified: boolean): void => {
    setOpen(false);
    setBusy(false);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(verified);
  }, []);

  const verify = useCallback((): void => {
    setBusy(true);
    setError(null);
    void signInWithPasskey()
      .then(() => finish(true))
      .catch((err: unknown) => {
        setBusy(false);
        setError(
          err instanceof Error ? err.message : "Re-verification failed.",
        );
      });
  }, [finish, signInWithPasskey]);

  return (
    <Modal onClose={() => finish(false)} open={open} title="Confirm it's you">
      <div className={styles.body}>
        <div className={styles.heading}>Confirm it&apos;s you</div>
        <div className={styles.text}>
          This is a sensitive action. Re-verify with your passkey to continue.
        </div>
        {error ? <div className={styles.error}>{error}</div> : null}
        <div className={styles.actions}>
          <Button onClick={() => finish(false)} variant="ghost">
            Cancel
          </Button>
          <Button disabled={busy} onClick={verify} variant="accent">
            {busy ? "Verifying…" : "Verify with passkey"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
